import { Editor } from '@tiptap/react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { ColorSwatch } from 'shared/components/pickers/ColorSwatch';
import { ThemeColorPicker } from 'shared/components/themes/ThemeColorPicker';
import { convertCssValueToHexaColor } from 'shared/utils/convertCssValueToHexaColor';
import { isVar } from 'shared/utils/isVar';

import classes from './TextColorPicker.module.scss';
import { useTextStyles } from '../../../../utils/tiptap/useTextStyles';
import { WidgetDefaultStyles } from '../../../../utils/typographyStyles';

type TextColorPickerProps = {
  setColorPickerRef?: Dispatch<SetStateAction<HTMLDivElement | null>>;
  editor: Editor | null;
  defaultStyles?: WidgetDefaultStyles;
  withinPortal?: boolean;
};

export function TextColorPicker(props: TextColorPickerProps) {
  const { editor, defaultStyles, withinPortal = false } = props;

  const { getTextStyles } = useTextStyles(editor);

  const [activeColor, setActiveColor] = useState(
    getTextStyles('color', {
      defaultStyles
    })
  );

  useEffect(() => {
    const updateActiveColor = () => {
      const currentActiveColor = getTextStyles('color', {
        defaultStyles
      });
      setActiveColor(currentActiveColor);
    };

    updateActiveColor();

    editor?.on('selectionUpdate', updateActiveColor);
    editor?.on('update', updateActiveColor);

    return () => {
      editor?.off('selectionUpdate', updateActiveColor);
      editor?.off('update', updateActiveColor);
    };
  }, [editor, getTextStyles, defaultStyles]);

  const handleActiveColorChange = (color: string) => {
    editor?.commands.setColor(color);
  };

  const resolvedColor = isVar(activeColor) ? convertCssValueToHexaColor(activeColor) : activeColor;

  return (
    <ThemeColorPicker
      color={resolvedColor}
      onSelectColor={colorObject => handleActiveColorChange(colorObject.color)}
      renderPopoverTarget={props => <ColorSwatch className={classes.popoverTarget} {...props} />}
      withinPortal={withinPortal}
    />
  );
}
